import { Key, useMemo } from 'react'

import { useMemoizedFn, useRequest } from 'ahooks'
import { Cascader } from 'antd'
import { isArray, isNil, last } from 'lodash'

import { queryAreaTreeApi } from '@eastcoal/api/system/area/queryAreaTree'

import { DataEmpty } from '@eastcoal/common/components/DataEmpty'
import { DataError } from '@eastcoal/common/components/DataError'
import { findTreeMatches, findTreePaths } from '@eastcoal/common/utils/array'
import { pick } from '@eastcoal/common/utils/object'

import { AreaCascaderProps } from './interface'

const fieldNames = {
  value: 'id',
  label: 'name',
  children: 'children'
} as const

const displayRender = (labels: string[]) => labels[labels.length - 1]

export const AreaCascader = ({
  value,
  onChange,
  multiple = false,
  ...restProps
}: AreaCascaderProps) => {
  const selectedKeys = useMemo(() => {
    if (isNil(value)) return []
    const values = isArray(value) ? value : [value]
    return values.map(item => `${item?.id}`)
  }, [value])
  const {
    data = [],
    error,
    loading,
    refreshAsync
  } = useRequest(() => queryAreaTreeApi({ enable: true }), {
    manual: false
  })

  const finalValue = useMemo(() => {
    if (isNil(data)) {
      return undefined
    }

    const nodesArr = selectedKeys.map(key =>
      findTreePaths(data, node => node.id === key)
    )
    const paths = nodesArr.map(nodes => nodes.map(node => `${node.id}`))

    const keys = multiple ? paths : paths[0]

    return keys as any
  }, [data, multiple, selectedKeys])

  const handleChange = useMemoizedFn((keys?: Key[] | Key[][]) => {
    // 清空
    if (isNil(keys) || keys.length === 0) {
      onChange?.(undefined)
    }
    // 多选
    else if (multiple) {
      const lastKeys = (keys as Key[][]).map(item => `${last(item)}`)
      onChange?.(
        findTreeMatches(data, lastKeys, (item, key) => item.id === key).map(
          item => pick(item, ['id', 'name', 'code'])
        )
      )
    } else {
      // 单选
      const lk = last(keys as string[])
      const values = findTreeMatches(
        data,
        [lk],
        (item, key) => item.id === key
      ).map(item => pick(item, ['id', 'name', 'code']))

      onChange?.(values[0])
    }
  })

  return error ? (
    <DataError error={error} reload={refreshAsync} />
  ) : (
    <Cascader
      changeOnSelect
      showSearch
      displayRender={displayRender}
      expandTrigger='hover'
      fieldNames={fieldNames}
      loading={loading}
      multiple={multiple}
      notFoundContent={<DataEmpty loading={loading} reload={refreshAsync} />}
      options={data}
      showCheckedStrategy={Cascader.SHOW_CHILD}
      value={finalValue}
      onChange={handleChange}
      {...restProps}
    />
  )
}
